import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, X, TrendingUp, Clock, DollarSign, GraduationCap, Briefcase, BarChart, Heart, Target, Award, Building2, MapPin } from "lucide-react";

const CareerComparison = () => {
  const [careers, setCareers] = useState([]);
  const [selectedCareers, setSelectedCareers] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      const response = await axios.get(`${API}/careers`);
      setCareers(response.data);
    } catch (error) {
      console.error("Error fetching careers:", error);
      toast.error("Failed to load careers");
    }
  };

  const handleAddCareer = (careerId) => {
    if (selectedCareers.length >= 4) {
      toast.error("Maximum 4 careers can be compared");
      return;
    }
    
    if (selectedCareers.find(c => c.id === careerId)) {
      toast.error("Career already selected");
      return;
    }
    
    const career = careers.find(c => c.id === careerId);
    setSelectedCareers([...selectedCareers, career]);
  };

  const handleRemoveCareer = (careerId) => {
    setSelectedCareers(selectedCareers.filter(c => c.id !== careerId));
    if (selectedCareers.length <= 2) {
      setComparisonData(null);
    }
  };

  const handleCompare = async () => {
    if (selectedCareers.length < 2) {
      toast.error("Select at least 2 careers to compare");
      return;
    }

    setLoading(true);
    try {
      const ids = selectedCareers.map(c => c.id).join(",");
      const response = await axios.get(`${API}/careers/compare/multiple?ids=${ids}`);
      setComparisonData(response.data);
      toast.success("Comparison generated successfully!");
    } catch (error) {
      console.error("Error comparing careers:", error);
      toast.error("Failed to compare careers");
    } finally {
      setLoading(false);
    }
  };

  const getCriterionIcon = (criterion) => {
    const icons = {
      title: <Briefcase className="w-4 h-4" />,
      category: <Target className="w-4 h-4" />,
      salary_range_india: <DollarSign className="w-4 h-4 text-green-600" />,
      salary_range_global: <DollarSign className="w-4 h-4 text-blue-600" />,
      timeline_to_enter: <Clock className="w-4 h-4" />,
      required_education: <GraduationCap className="w-4 h-4" />,
      skills: <Target className="w-4 h-4" />,
      certifications: <Award className="w-4 h-4" />,
      job_outlook: <TrendingUp className="w-4 h-4" />,
      work_life_balance: <Heart className="w-4 h-4" />,
      growth_potential: <BarChart className="w-4 h-4" />,
      work_environment: <MapPin className="w-4 h-4" />,
      top_companies: <Building2 className="w-4 h-4" />
    };
    return icons[criterion] || <ArrowRight className="w-4 h-4" />;
  };

  const formatCriterionName = (criterion) => {
    // Special formatting for salary ranges
    if (criterion === 'salary_range_india') return 'Salary Range (India)';
    if (criterion === 'salary_range_global') return 'Salary Range (Global)';
    if (criterion === 'top_companies') return 'Top Companies';
    if (criterion === 'work_environment') return 'Work Environment';
    
    return criterion
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderCriterionValue = (career, criterion) => {
    const value = career[criterion];
    
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {typeof item === 'object' ? item.phase || JSON.stringify(item) : item}
            </Badge>
          ))}
        </div>
      );
    }
    
    // Special styling for salary ranges
    if (criterion === 'salary_range_india') {
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs md:text-sm font-semibold">
            {value || 'N/A'}
          </Badge>
        </div>
      );
    }
    
    if (criterion === 'salary_range_global') {
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs md:text-sm font-semibold">
            {value || 'N/A'}
          </Badge>
        </div>
      );
    }
    
    if (criterion === 'work_life_balance') {
      const colors = {
        'Very Good': 'bg-success/10 text-success border-success/20',
        'Good': 'bg-primary/10 text-primary border-primary/20',
        'Moderate': 'bg-warning/10 text-warning border-warning/20',
        'Challenging': 'bg-destructive/10 text-destructive border-destructive/20'
      };
      return <Badge variant="outline" className={`${colors[value] || ''} text-xs md:text-sm`}>{value}</Badge>;
    }
    
    if (criterion === 'growth_potential') {
      const colors = {
        'Very High': 'bg-purple-100 text-purple-800 border-purple-200',
        'High': 'bg-primary/10 text-primary border-primary/20',
        'Moderate': 'bg-warning/10 text-warning border-warning/20',
        'Limited': 'bg-muted text-muted-foreground border-border'
      };
      return <Badge variant="outline" className={`${colors[value] || ''} text-xs md:text-sm`}>{value}</Badge>;
    }
    
    return <span className="text-xs md:text-sm text-gray-700">{value || 'N/A'}</span>;
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Career Path Comparison</h1>
          <p className="text-sm md:text-base text-gray-600">Compare up to 4 careers side-by-side to make informed decisions</p>
        </div>

        {/* Career Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl">Select Careers to Compare</CardTitle>
            <CardDescription className="text-sm">Choose 2-4 careers to see detailed comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <Select onValueChange={handleAddCareer}>
                  <SelectTrigger className="w-full sm:flex-1">
                    <SelectValue placeholder="Select a career..." />
                  </SelectTrigger>
                  <SelectContent>
                    {careers.map((career) => (
                      <SelectItem key={career.id} value={career.id}>
                        {career.title} ({career.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleCompare}
                  disabled={selectedCareers.length < 2 || loading}
                  className="w-full sm:w-auto"
                >
                  {loading ? "Comparing..." : "Compare Careers"}
                </Button>
              </div>

              {/* Selected Careers */}
              {selectedCareers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedCareers.map((career) => (
                    <Badge key={career.id} variant="outline" className="px-3 py-2 text-xs md:text-sm">
                      <span className="truncate max-w-[150px] md:max-w-none">{career.title}</span>
                      <button
                        onClick={() => handleRemoveCareer(career.id)}
                        className="ml-2 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Comparison Results - Desktop Table View */}
        {comparisonData && (
          <>
            {/* Desktop View - Table */}
            <Card className="hidden md:block">
              <CardHeader>
                <CardTitle>Career Comparison Results</CardTitle>
                <CardDescription>Side-by-side comparison of selected careers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left p-4 font-semibold bg-gray-100 sticky left-0 z-10">
                          Criteria
                        </th>
                        {comparisonData.careers.map((career, idx) => (
                          <th key={idx} className="p-4 text-left font-semibold bg-primary/10">
                            <div className="min-w-[200px]">
                              <div className="text-lg font-bold text-primary">{career.title}</div>
                              <Badge variant="outline" className="mt-1">{career.category}</Badge>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.comparison_criteria.filter(c => c !== 'title' && c !== 'category').map((criterion, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="p-4 font-medium bg-muted sticky left-0 z-10">
                            <div className="flex items-center gap-2">
                              {getCriterionIcon(criterion)}
                              <span className="text-sm">{formatCriterionName(criterion)}</span>
                            </div>
                          </td>
                          {comparisonData.careers.map((career, careerIdx) => (
                            <td key={careerIdx} className="p-4">
                              {renderCriterionValue(career, criterion)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Mobile View - Card Layout */}
            <div className="md:hidden space-y-4">
              <div className="bg-white rounded-lg p-4 shadow-sm border">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Career Comparison Results</h2>
                <p className="text-sm text-gray-600 mb-4">Swipe through careers to compare</p>
              </div>

              {comparisonData.careers.map((career, careerIdx) => (
                <Card key={careerIdx} className="shadow-md">
                  <CardHeader className="bg-primary/5 pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{career.title}</CardTitle>
                        <Badge variant="outline" className="text-xs">{career.category}</Badge>
                      </div>
                      <Badge className="bg-primary text-white">
                        {careerIdx + 1}/{comparisonData.careers.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-4">
                      {comparisonData.comparison_criteria
                        .filter(c => c !== 'title' && c !== 'category')
                        .map((criterion, idx) => (
                          <div key={idx} className="border-b pb-3 last:border-b-0">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                {getCriterionIcon(criterion)}
                              </div>
                              <span className="font-semibold text-sm text-gray-700">
                                {formatCriterionName(criterion)}
                              </span>
                            </div>
                            <div className="ml-10">
                              {renderCriterionValue(career, criterion)}
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Empty State */}
        {!comparisonData && selectedCareers.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Target className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Start Comparing Careers</h3>
              <p className="text-gray-600 mb-4">
                Select 2-4 careers from the dropdown above to see a detailed side-by-side comparison
              </p>
              <p className="text-sm text-gray-500">
                Compare salary, education requirements, timeline, skills, and more!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CareerComparison;
